/**
 * "Fill out a JSA before the crew starts" — the walkthrough the owner asked for, on 2026-08-09, in
 * these words: *"How do crews fill out a jsa. I guess I am unsure of that workflow."*
 *
 * ══ WHAT THE OWNER FOUND WHEN HE FOLLOWED IT ═════════════════════════════════════════════════════
 * Two reports, a minute apart, and they are one fault:
 *
 *   "the show me how for JSA's skips alot of steps. by the time you get to the submit button half of
 *    the form is empty."
 *   "after typing names when it asks for names, it immediately jumps to the next step. same for the
 *    drop down boxes."
 *
 * THE SECOND CAUSED THE FIRST. Every gate on this sheet was `until: { filled: X }`, which asks the
 * DOM whether the first input inside the anchored cell carries text. A picker keeps its in-flight
 * SEARCH TEXT in that same box (SearchableSelect.jsx:34, ProjectPicker.jsx:139), so typing "Jo"
 * satisfied the gate, the card advanced while he was still reading the list, HE NEVER TAPPED A NAME,
 * and the field ended up EMPTY — with the walkthrough having told him it was done. Four required
 * boxes went past him that way.
 *
 * THE PREVIOUS VERSION OF THIS FILE KNEW, and argued it was bounded: the field is required, so the
 * submit would refuse and the reader would be stopped one card later. THAT REASONING IS DISPROVED,
 * and it is disproved by the first report — the bound was the 422 at the end, and the 422 at the end
 * is precisely the experience being complained about. A walkthrough that carries you to a Submit the
 * form refuses has taught the wrong thing twice: that the sheet is done, and that the app is broken.
 *
 * SO A GATE NOW READS THE ANSWER, NOT THE BOX. `until: { answered: … }` asks the component holding
 * the answer (`data-tour-answered`, published by CanvasRenderer from validate.js's `isAnswered`), so
 * a search string is not an answer, an empty table row is not an answer, and a value the sheet would
 * reject is not an answer either. `filled` still exists and is still right where the typing IS the
 * answer — the badge-number box in the field-PIN tour is the one place any tour uses it now.
 *
 * ...AND IT WAITS FOR THE SENTENCE TO BE FINISHED. That fixed the pickers and left the plain text
 * boxes, because a text box's answer IS its text: after one character `scope_of_work` holds an
 * answer, so the card advanced on "B", the overlay pulled focus onto itself, and the browser sent
 * "reak the pavement out over the main" to the card instead of to the sheet. Submit then ACCEPTED
 * the one-letter sheet — the first report again, wearing the disguise of a finished form. Both
 * halves are in the engine rather than in this file, so no tour can reintroduce either:
 * gateDetectors holds a satisfied gate while somebody is still typing into the box that satisfied it
 * (and releases it the instant they click, tab, blur or stop), and TourOverlay no longer takes focus
 * off a control somebody is writing in.
 *
 * ══ AND IT GATES ON THE WHOLE SHEET, NOT ON SIX BOXES OF IT ══════════════════════════════════════
 * The other half of his first sentence was COVERAGE, and it was worse than the false positives. The
 * server refuses an empty JSA with SEVENTEEN missing required boxes (measured: submitted an empty one
 * against real Postgres and read the 422). This tour used to name six fields and gate on four of
 * them, and only TWO of those four were required — so the number of required questions it reliably
 * held anybody for was zero, and `send-it` then waited for a submit the sheet would never issue.
 *
 * A card may therefore name a SET: `until: { answered: [ …anchors ] }`. One card per section of the
 * paper form, gating on every required box in that section, because seventeen one-field cards would
 * be honest and unbearable and three obvious text boxes do not need three cards. The card says which
 * of them are still blank, live, as they are filled in (TourOverlay's outstanding list) — "fill in
 * this section" is useless on a section with seven boxes and one gap.
 *
 * THE SEVENTEEN, and which card holds each:
 *   the-job              project
 *   the-rest-of-the-job  jsa_date·, jsa_start_time, crew, exact_location, supervisor_on_site·,
 *                        scope_of_work                            (· = arrives pre-filled, see below)
 *   emergency            nearest_medical_facility, medical_distance_time, muster_point,
 *                        who_calls_911, who_meets_ems, who_brings_first_aid, first_aid_cpr_current
 *   one-signature        supervisor_signature
 *   changed-conditions   conditions_changed, stop_work_used
 *
 * FOUR MORE BECOME REQUIRED ONCE THEIR TRIGGER IS ANSWERED — first_aid_cpr_exception (if first aid
 * is "no"), change_rebriefed and conditions_changed_detail (if anything changed), stop_work_detail
 * (if stop-work was used) — so a real fill can demand up to twenty-one. They are named by the same
 * cards, and a question that is not being asked does not hold anybody: an anchor that is not on the
 * page reads as `unasked` rather than as unanswered (gateDetectors.answerState).
 *
 * TWO OF THE SEVENTEEN ARRIVE PRE-FILLED and are gated anyway. `jsa_date` carries `default: "@today"`
 * and `supervisor_on_site` carries `default: "@me"` — but @me resolves to nothing at all when the
 * login is not tied to an employee record (FormFill.resolveDefault), which is a real state and one
 * the reader would otherwise meet at the Submit button. Gating them costs a reader with both nothing
 * (the set is satisfied by the five boxes beside them) and catches the one who has neither.
 *
 * TWO STEPS STILL CANNOT BE GATED ON WHAT THEY ARE ABOUT, and each says so at the step:
 * `ppe-and-controls` and `site-controls` point at the sheet's two tick-box lists, and NEITHER field
 * is required — a gate demanding a tick would teach the opposite of the steps' own sentences and
 * would make a crew that genuinely needs no site controls tick something untrue to finish the
 * walkthrough. They advance on Next. They used to advance on a CLICK, which was worse than either:
 * `clicked` binds to the anchored element, the anchor is the whole paper cell, and a tap on the
 * cell's label or on white space beside a checkbox advanced the card with nothing ticked — a gate
 * that looked like a gate, certified an act that had not happened, and taught nothing. `one-signature`
 * used to be in the same position for a different reason and no longer is — the pad's answer is a
 * base64 PNG in the payload, and the payload is exactly what `answered` reads, so the one required
 * box that COMPLETES the sheet is now held properly instead of being waved through by a tap.
 *
 * THE TWO TABLES ARE NOT REQUIRED — `task_hazard_control` and `crew_signoff` both carry
 * `required: false`, and neither appears in the server's 422. Their cards are gated all the same,
 * because they are what an inspector actually reads and what the crew signs; they buy nothing toward
 * Submit and they are the practice this sheet exists for. A previous note here claimed
 * `crew_acknowledged` being required would make a seeded roster unsubmittable: that is FALSE, and
 * measured to be. `CanvasSchema.iter_fields` (schemas/form_schema.py:818) yields only top-level
 * `kind=='field'` elements and never descends into a table's `fields`, so NO table column's
 * `required` is enforced anywhere — submitting with both tables entirely absent returns the same
 * seventeen and nothing else. The reason not to pre-print the roster is the one that follows it: a
 * briefing nobody confirmed.
 *
 * THE FIRST DRAFT ASKED FOR A SIGNAL THAT WAS NEVER ADDED — `{ type: 'rows', field: …, min: 1 }`,
 * "the table has at least one row". `answered` is that demand and a stricter one: a table is answered
 * only when some row has something IN it, because "+ Add row" pushes `{}` and an empty row is not a
 * hazard analysis.
 *
 * ══ WHAT A PRACTICE RUN HAD TO BE TAUGHT BEFORE ANY OF THIS WAS WALKABLE ═════════════════════════
 * Opening this sheet is a WRITE: FormFill POSTs a draft submission before a character is typed and
 * renders off the answer. Practice mode blocks writes, so under the generic answer the sheet came up
 * as "This form has already been submitted, so it's read-only" over "This form has no fields yet" —
 * no fields, no Submit button, and every step that lives on the sheet pointing at nothing. That is fixed in
 * shared/tour/practiceResponses.js (a draft that says `status: 'draft'`, plus the PATCH and the
 * submit), and it is held there by "a practice run on the JSA sheet" in
 * shared/tour/__tests__/tourWalkthrough.test.jsx.
 *
 * ══ WHO THIS IS FOR ══════════════════════════════════════════════════════════════════════════════
 * Anyone who can fill a form, which under the seeded roles is master_admin and admin (blanket
 * bypass), manager, superintendent, foreman, estimator, office_staff and employee. In practice it is
 * a foreman-or-above job and step `emergency` says why in plain words.
 *
 * NOT role-gated to foreman, deliberately. The person who needs it most is the OWNER, who is an
 * admin — and an admin's Forms tab opens on Templates, not on the crew's screen (FormsModule.jsx:29),
 * which is very likely why he could not find this workflow. Step 1 is that redirect, said out loud.
 *
 * AND STEP 1 IS NOW A CARD THE CREW CAN ACTUALLY READ. It waited for the crew's list to APPEAR, which
 * is a state gate, and a state gate that is already true satisfies on arrival — which for a foreman
 * it always is, because /forms sends anyone with forms:submit and no forms:design straight to
 * /forms/fill. So the walkthrough opened on card 2 of 16 for its whole real audience and the sentence
 * about the QR sticker reached only the admins who land on Templates. It waits for the TAP now; see
 * the note on the step.
 *
 * ══ WHAT THIS TOUR REFUSES TO PRETEND ════════════════════════════════════════════════════════════
 * Seven things about this workflow are broken, missing or surprising, and every one of them is IN a
 * step rather than smoothed over. A crew that meets them for the first time at a job box at 6am is
 * the failure this tour exists to prevent.
 *
 *   1. A JSA CANNOT BE PUT ON A QR STICKER. Verified by running it: the server refuses to issue one,
 *      with "'Job / project' picks from a list of jobs, and it's required. A public sticker page
 *      can't show that, so this form can't be used on a sticker."
 *      (`template_is_publicly_fillable`, forms services/sticker_submit.py:201, called at issue time in
 *      routes/forms_settings.py:231. The JSA carries five required directory-backed fields — project,
 *      supervisor_on_site, who_calls_911, who_meets_ems, who_brings_first_aid.) The Equipment Pre-Op
 *      starter passes the same gate, which is the whole contrast: one starter is scannable and this
 *      one can only be reached by someone logged in.
 *   2. THE PEOPLE PICKERS ARE NOT THE WHOLE COMPANY. Every employee dropdown is fed by
 *      GET /api/employees/, which filters to `Employee.id == employee_id` for any caller without
 *      hr:view_all (hr backend api/employees.py:169-176) and department-scopes everyone who is not a
 *      manager or an admin. The seeded `employee` role holds forms:submit and NOT hr:view_all
 *      (identity session.py:416-421), so a field hand sees exactly one name — their own — and the JSA
 *      needs four different people named.
 *   3. THE CREW ROSTER DOES NOT POPULATE — and the sheet no longer says it does. It used to print
 *      "The roster is populated from the crew selected in section 1", which nothing did. Auto-filling
 *      it would be wrong, not merely unbuilt: the roster is who is ON the crew, the table is who was
 *      BRIEFED, and pre-printing names asserts a briefing nobody confirmed. Corrected wording — "Add
 *      one row per person briefed" — ships as JSA v2 in
 *      2026_08_17_reseed_starter_jsa_crew_signoff_wording.sql. A tenant who had already adapted their
 *      copy is skipped by the reseed guard and keeps the old sentence. (The version of this note that
 *      also argued seeded rows would make the sheet UNSUBMITTABLE was wrong — see the header: a table
 *      column's `required` is enforced nowhere.)
 *   4. THERE IS NO SIGNATURE PER CREW MEMBER, and there cannot be — a table column may not be a
 *      signature (the field registry refuses it; test_starter_templates.py asserts it). Each person
 *      gets a checkbox. One drawn signature exists on the sheet: the supervisor's.
 *   5. A LONG CREW RUNS OUT OF ROOM. Every canvas cell is `overflow-hidden` with no vertical scroll
 *      (CanvasRenderer), so a table taller than its designed height is CLIPPED, not scrolled. The
 *      seed's own comment says so. Past nine or ten rows the "+ Add row" button is below the cut and
 *      cannot be reached, and nothing warns.
 *   6. YOU CANNOT LOOK ONE UP BY JOB OR BY DATE. The Submissions screen filters by status and by form
 *      name, and by nothing else (SubmissionList.jsx:136-155). The template flags the job field
 *      `promote_for_search`, and the table that would make that work — form_submission_values — is
 *      never written to by anything: verified empty (0 rows) after a real submit against Postgres.
 *      (The LIST ENDPOINT already accepts context / reference_type / reference_id filters
 *      — form_submissions.py:113-121 — which is the cheapest door into fixing this. No screen sends
 *      them.)
 *   7. NOTHING CHECKS THAT THE THREE EMERGENCY NAMES ARE THREE DIFFERENT PEOPLE. The sheet takes one
 *      name in all three boxes and sends it; so does this walkthrough, whose gate asks each box
 *      independently whether it holds an answer. That is deliberate — see the note on `emergency`: a
 *      distinctness rule would dead-end the plain-employee login that same card is warning about, who
 *      is offered exactly one name. So the card says it is on the reader rather than implying the app
 *      is watching.
 *
 * ══ TWO THINGS THE FIRST DRAFT OF THIS COPY GOT WRONG ════════════════════════════════════════════
 * Both were inherited from the written survey and both were REFUTED by running the code. They are
 * recorded because each was wrong in the direction that invents a problem, and a tour that warns a
 * foreman about a wall that is not there costs the same trust as one that hides a wall that is.
 *
 *   • "The job picker is department-scoped, and only shows open jobs." NEITHER IS TRUE. The JSA's
 *     project_select renders shared/components/ProjectPicker, which sends
 *     `ignore_department: true` and no status filter at all — observed on the wire:
 *     GET /api/projects/?limit=50&sort_by=project_number&ignore_department=true. The route only
 *     filters by department when that flag is absent (projects routes/projects.py:380) and only
 *     filters `is_active` when the caller passes it (:353). So a foreman can search EVERY job in the
 *     tenant — including finished and cancelled ones, which is the risk actually worth naming, and
 *     is what `the-job` now says.
 *   • "A foreman can start a review but cannot approve his own sheet." The second half is right for
 *     the wrong reason: it is not about it being HIS. A foreman simply has no approve grant, so
 *     Approve answers 403 for any sheet (run live). The copy no longer implies self-approval is the
 *     rule being enforced, because for an admin there is no such rule.
 *
 * Everything else in the copy was checked the same way. The seventeen required boxes are the seventeen
 * the server really returns — re-measured against the CURRENT document (starter version 4,
 * 2026_08_19_reseed_starter_jsa_readable.sql) rather than carried over: an empty JSA published
 * through the real API 422s with exactly those seventeen keys and an empty `invalid` list, and
 * src/shared/forms_starter builds a schema dict-equal to that migration's blob, so the Python source
 * of truth and the seeded document are the same document. The PDF is a real 200 application/pdf;
 * submitting notifies roles ["admin", "master_admin"] IN-APP ONLY with send_email=False
 * (submission_service.py:198, :226), which is why `send-it` says somebody has to tell the safety
 * officer; and the list really is newest-first (`order_by(created_at.desc())`,
 * form_submissions.py:140).
 *
 * ══ WHERE IT STOPS ═══════════════════════════════════════════════════════════════════════════════
 * At the PDF, which is the artefact you hand an inspector. Approving a submitted sheet is a different
 * job for a different desk — and the person taking this tour usually cannot do it: a foreman holds
 * forms:review but not forms:approve, and Approve answers him 403 (run against the live API).
 *
 * NO STEP LIVES ON THE "Submitted ✓ #14" CONFIRMATION SCREEN, and that is a consequence of the
 * practice-run rule rather than an oversight: that screen only exists after a write, and on a practice
 * run it carries no number (practiceResponses.js will not invent a sequence value it cannot give
 * back). A step anchored there would be missing the one thing it was about, for exactly the people
 * rehearsing. What the number is and what it is for is said on `send-it` instead, where it is true.
 *
 * THE SAME RULE COSTS THIS TOUR ONE STEP, AND IT IS `open-one`. The Submissions list a practice run
 * arrives at holds whatever the tenant already had — not the sheet just "sent", which was never
 * created. On a tenant that has never had a form filled in there is nothing to open, and that step
 * waits out its stuck timer. It is the only step of the five tours that a practice run can strand
 * somebody on, its hint says so in plain words, and the alternative — letting the walkthrough file a
 * real JSA so the list has something in it — is the exact trade the owner ruled out.
 *
 * ══ ONE PRECONDITION THAT IS NOT THIS TOUR'S TO FIX ═══════════════════════════════════════════════
 * A tenant created before 2026-08-07 has no Forms tab for anybody, because the login response fills
 * `accessible_modules` from role_module_defaults and `forms` was in neither that table nor identity's
 * role seed. scripts/migrations/2026_08_07_seed_forms_module_access.sql backfills it. Until it has
 * run, `module: 'forms'` correctly removes this tour from the launcher — so the person who most needs
 * to be told is the one who cannot be told by a tour. It is in the walkthrough's written notes.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

export default defineTour({
  id: 'jsa-fill',
  title: 'Fill out a JSA before the crew starts',
  summary: 'The safety sheet your crew signs at the job box — where it lives, and where it fights you.',
  audience: AUDIENCE.ONBOARDING,
  module: 'forms',
  // GATED AS A WHOLE. Every step up to `send-it` needs this grant; the four on the Submissions screen do
  // not, because that screen is bare auth (it just narrows the list to your own). Without a tour-level
  // gate those four would survive the per-step filter and the launcher would advertise a "4 steps"
  // tour that opens on a list, skips the entire form, and teaches nobody how a JSA is filled out.
  permission: 'forms:submit',

  steps: [
    {
      id: 'open-fill',
      anchor: 'forms.nav.fill',
      route: '/forms',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Start here — and not at a QR sticker',
      body: 'A JSA is the sheet the crew fills in before the work starts: what you are doing, what can hurt somebody, and what you are doing about it. It cannot go on a QR sticker like the machine inspection can, because it asks you to pick a real job and real people off a list and a stranger with a phone has no list. So whoever fills it in has to be logged in. Tap “Fill a form” — that is the crew’s screen, and it is the tab a JSA is filled in from. If you landed on Templates, that is the form designer; the crew never goes there.',
      /**
       * IT WAITS FOR THE TAP, AND IT USED TO WAIT FOR THE CREW'S SCREEN TO EXIST — WHICH MEANT THE
       * PEOPLE THIS TOUR IS FOR NEVER READ THIS CARD.
       *
       * `until: { appears: 'forms.fill.list' }` is a STATE signal, and a state signal that is already
       * true when the step opens satisfies at once (gateDetectors' header — deliberate, and right
       * nearly everywhere). Now read it against what /forms does with a foreman: FormsHome sends
       * anyone with forms:submit and no forms:design straight to /forms/fill (FormsModule.jsx:28-33).
       * The list was therefore on screen before this card had been drawn, the card was consumed in
       * the same frame, and the walkthrough OPENED ON CARD 2 for its entire real audience. Measured:
       * a foreman's first card was `pick-the-sheet`, numbered 2 of 16, with nothing behind it. An
       * admin, who lands on Templates, saw card 1 — so the one sentence saying a JSA cannot go on a
       * QR sticker reached only the people who were never going to fill one in.
       *
       * `clicked` is an EVENT and cannot self-satisfy: an event that happened before the step opened
       * is not evidence anybody read anything. The cost is one tap on a tab a foreman is already
       * standing on, which navigates nowhere and teaches him where that tab is. The cost of the other
       * arrangement was the card.
       *
       * THE CASCADE THE OLD NOTE FEARED IS ALREADY COVERED, which is what makes this safe: it warned
       * that a click which did not land would leave every later step spotlighting controls on pages
       * the reader was never on. `pick-the-sheet` carries `route: '/forms/fill'` and the engine
       * navigates to a step's route on arrival (TourProvider's route sync), so the screen is reached
       * whether or not the tap did anything.
       */
      advanceOn: ADVANCE_ON.CLICK,
      stuckHint: 'No “Fill a form” tab? Then this login cannot fill forms in — that is a permission the office grants.',
    },
    {
      id: 'pick-the-sheet',
      anchor: 'forms.fill.list',
      route: '/forms/fill',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Pick the sheet',
      body: 'Every form your company has published is here, with a search box above. Two come with the system: this one and the Equipment Pre-Operation Inspection. Tap “Job Safety Analysis (Starter)”. It is a starting point — copy it and change the wording to match how you actually work.',
      // Waits for the SHEET, not for the tap. Opening a sheet also opens a draft on the server, and
      // that can fail — an unpublished template, a lost connection — in which case the reader is
      // looking at "Couldn't open this form" and the ten steps that live on the sheet are all
      // pointing at nothing. `forms.fill.status` is the sheet's own title bar, so it exists only when
      // the sheet really opened.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'forms.fill.status' },
      stuckHint: 'Nothing in the list? Then nobody has published a form yet — a JSA has to be published before a crew can fill it.',
    },
    {
      id: 'it-saves-itself',
      // No `route`. This step and the nine after it live on /forms/fill/<the sheet's id>, which is a
      // real id only at runtime — the engine navigates to `route` verbatim, so any literal here would
      // walk the user off the sheet they just opened. They get here by the tap in the step above.
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.status',
      module: 'forms',
      permission: 'forms:submit',
      title: 'It is already saved',
      body: 'Opening the sheet started it — there is no Save button, because you do not save it. Your answers go up about a second after you stop typing and this corner tells you whether they landed. A couple of boxes are filled in for you already. Put the tablet down and walk away and the half-done sheet is waiting at the top of the last screen under “Continue where you left off”.',
      // Explain-only, and honestly so: there is nothing on this step to do. It points at a status
      // indicator to say what it means.
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'the-job',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.project',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Which job — and read it before you tap it',
      body: 'Type any part of the job number, the job name or the client’s name; it searches as you type, against every job in the company, not only your department’s. It does not hide jobs that are finished or cancelled, and until you type you are only looking at the first fifty by number — so read what you are tapping. A JSA filed against last year’s job is worse than no JSA, because it looks like a record. Typing is not choosing: this card waits for you to actually tap the job out of the list.',
      // Waits for A JOB TO HAVE BEEN CHOSEN, which is not the same as the box having text in it —
      // this is the field the whole record hangs off, and it is the one the owner watched the tour
      // march past him.
      //
      // THE FALSE POSITIVE THIS REPLACES. `until: { filled: … }` read the picker's input, and
      // ProjectPicker shows its in-flight query in the same box that later shows the chosen job
      // (`displayText = open ? query : …`, ProjectPicker.jsx:139) — so the card advanced on the first
      // keystroke. The old note called that bounded, because the job is required and the submit would
      // refuse. It was not bounded: the refusal at the end IS the complaint. `answered` reads the
      // payload, where a query has never been.
      advanceOn: ADVANCE_ON.DO,
      until: { answered: 'forms.fill.field.project' },
      stuckHint: 'Typed something and the card has not moved? Tap the job in the list — a search box with words in it is not a job picked. Nothing coming up at all? Try just the number, and give it a second.',
    },
    {
      id: 'the-rest-of-the-job',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.jsa-start-time',
      module: 'forms',
      permission: 'forms:submit',
      title: 'The rest of the top block',
      body: 'Start time, the crew, exactly where you are — a street address or coordinates, because “the north job” is not a place an ambulance can find — who is supervising, and what the crew is actually doing today in plain words. Today’s date is already in, and so is Supervisor on site if your login is tied to your employee record; if that box is empty, that link is what is missing and the office sets it. This card lists whatever is still blank and waits for the lot.',
      /**
       * SIX BOXES, ONE CARD. Four of them are a line of text each and one is a date that is
       * already in — teaching those one at a time would be five cards saying nothing. The sixth,
       * scope of work, is the only one anybody has to think about, and it reads better next to the
       * location than alone.
       *
       * All six are REQUIRED, and before this card existed none of them was mentioned anywhere in
       * this tour — five of the thirteen boxes the reader met for the first time at a Submit that
       * refused him. The two carrying defaults are in the set on purpose: @today never fails, @me
       * fails silently for anyone whose login has no employee record, and this is the card where
       * that is worth finding out.
       */
      advanceOn: ADVANCE_ON.DO,
      until: {
        answered: [
          'forms.fill.field.jsa-date',
          'forms.fill.field.jsa-start-time',
          'forms.fill.field.crew',
          'forms.fill.field.exact-location',
          'forms.fill.field.supervisor-on-site',
          'forms.fill.field.scope-of-work',
        ],
      },
      stuckHint: 'The card names what is still blank. If it is Supervisor on site, this login is not tied to an employee record — pick yourself out of the list by hand, and ask the office to link it.',
    },
    {
      id: 'emergency',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.who-calls-911',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Name three people, before anything happens',
      body: 'Who calls 911, who walks out to meet the ambulance, who fetches the kit and the AED. Three different people, decided now, while everyone is standing together and nobody is hurt. Nothing in the app checks that they are three different people — it will take your own name in all three boxes and send it — so that part is on you. Above them: the nearest hospital, how far it is, and where everyone meets. And say whether anyone on site holds current first aid and CPR — answer no and a box opens asking who and why, because work does not start without that written down. Now the catch — these name lists are not the whole company. A foreman gets his own department; somebody on a plain employee login gets exactly one name, his own. That is why a JSA is a foreman’s job and not a hand’s: a hand cannot name three different people here, because the app will only offer him himself.',
      /**
       * THE WHOLE SECTION, and it is the card that changed most.
       *
       * It used to say "name three people" and gate on ONE of them — and gate on it through the
       * input, so typing three letters into the first box satisfied it and the other two were never
       * mentioned again by anything. Three of the seventeen went past the reader on this card alone.
       *
       * The first-aid exception is in the set and will usually not be on the page at all: it is
       * `visible_if first_aid_cpr_current == no`, so for a crew that is covered it is a question
       * nobody is being asked and it holds nobody. For a crew that is NOT covered it is required, it
       * is the most consequential box in the section, and it is the one somebody scrolls past.
       *
       * "THREE DIFFERENT PEOPLE" IS NOT CHECKED, AND THE CARD NOW SAYS SO. The gate asks each box
       * independently whether it holds an answer (gateDetectors.allAnswered); there is no
       * cross-field distinctness anywhere in the chain, and one name in all three boxes advances the
       * card and submits. Measured.
       *
       * It stays unchecked deliberately. A distinctness rule would strand exactly the person the
       * paragraph above is warning about — a plain employee login is offered ONE name, his own, so a
       * gate demanding three would be a dead end for the reader who most needs to be told why. The
       * honest fix for a claim the walkthrough cannot verify is to stop making it silently, so the
       * copy names it as the reader's job rather than the app's.
       */
      advanceOn: ADVANCE_ON.DO,
      until: {
        answered: [
          'forms.fill.field.nearest-medical-facility',
          'forms.fill.field.medical-distance-time',
          'forms.fill.field.muster-point',
          'forms.fill.field.who-calls-911',
          'forms.fill.field.who-meets-ems',
          'forms.fill.field.who-brings-first-aid',
          'forms.fill.field.first-aid-cpr-current',
          'forms.fill.field.first-aid-cpr-exception',
        ],
      },
      stuckHint: 'Only your own name in the list? That is the point of this step — this login cannot see the rest of the company, so it cannot fill in a JSA. The office grants that. And typing a name is not picking one: tap it in the list.',
    },
    {
      id: 'hazards',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.task-hazard-control',
      module: 'forms',
      permission: 'forms:submit',
      title: 'One line per step of the job',
      body: 'Tap “+ Add row” for each step of the job: what you are doing, what can hurt you, what stops it happening, and what limits the damage if it happens anyway. Anything that can kill — a fall, a cave-in, electrocution, struck-by, caught-in — needs that last column filled too, not just the thing that stops it. This is the part of the sheet an inspector actually reads. On a phone six columns will not fit, so each row stacks into a block of labelled boxes instead of a table — nothing is hidden, you scroll down instead of across. A tablet gives you the table, which is easier to read across.',
      // The first draft asked for `rows >= 1`. `answered` is that demand and stricter — see the
      // header: "+ Add row" pushes an empty row, and an empty row is not a hazard analysis, so this
      // holds until some row has something written in it.
      //
      // NOT REQUIRED BY THE SHEET, and that is deliberate rather than an oversight: this table is
      // what an inspector reads, so the card demands it even though the server would let it through.
      advanceOn: ADVANCE_ON.DO,
      until: { answered: 'forms.fill.field.task-hazard-control' },
      stuckHint: 'Add a row with “+ Add row” at the bottom of the table, then write the first task step into it.',
    },
    {
      id: 'ppe-and-controls',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.required-ppe',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Tick the gear',
      body: 'Tap everything the crew is wearing today. Worth knowing: the app does not force this one. You can send this sheet with nothing ticked and it will go through, so a blank list here means nobody answered, not that nothing applied. There is a second list under it for the site itself — that is the next card.',
      /**
       * THE ONE FIELD ON THE SHEET NO GATE MAY DEMAND, and the argument for that is unchanged: THE
       * SHEET ITSELF DOES NOT REQUIRE THIS FIELD, which is literally what the step's own sentence
       * teaches. `answered` would work perfectly — a multi_select's answer is its ticked values, and
       * the cell publishes it like every other — and a card that refused to move until something was
       * ticked would teach the opposite of the paragraph above it, and would leave a crew whose job
       * genuinely needs no site controls unable to finish the walkthrough without ticking something
       * untrue on a safety sheet.
       *
       * (`filled` would have been wrong for a second reason, worth keeping written down: it reads the
       * FIRST form control in the cell, which here is the "Hard hat" checkbox — so it would have
       * meant "Hard hat is ticked" and sat dead for anybody whose crew needs a different one.)
       *
       * WHAT CHANGED IS THE PRETENCE, NOT THE ARGUMENT. This was `ADVANCE_ON.CLICK`, which
       * tourSchema normalises to `until: { clicked: 'forms.fill.field.required-ppe' }` — and `clicked`
       * binds to the ANCHORED ELEMENT, which here is the whole paper cell. A tap on the cell's label
       * or on the white space beside a checkbox advanced the card with nothing ticked and nothing
       * read. Measured: a plain click on the cell moved the tour to the next card and the submitted
       * sheet carried no required_ppe entry at all.
       *
       * That is worse than an honest Next. It looks gated, so it claims the reader did something; it
       * is satisfied by an act that teaches nothing; and it is on the one card whose whole subject is
       * that the app is not checking. So the card now advances on Next and says why, exactly like
       * `it-saves-itself` and `what-you-can-filter-by`. The rule this tour is built on is that a
       * reader cannot click past a step that asks him to DO something. This step asks him to know
       * something.
       */
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'site-controls',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.site-controls',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Then tick the site',
      body: 'The same again for the job itself — 811 ticket, trench protection, lockout, spotter, and the rest. This one is not forced either, and it is the list an inspector reads next to the hazards you wrote above: a blank here beside “cave-in” in the table is the pair that gets asked about.',
      /**
       * A SECOND CARD BECAUSE THERE IS A SECOND FIELD, and it was previously named by the PPE card's
       * sentence and pointed at by nothing.
       *
       * `site_controls` is its own top-level multi_select with its own cell and its own anchor
       * (src/shared/forms_starter/_job_safety_analysis.py, section 5). The old copy said "do the same
       * in the list below it for the site itself" while the spotlight stayed on the PPE cell above —
       * so the reader was told about a list the walkthrough never showed him, on a sheet where the
       * two cells are separated by a section band and a page of tick-boxes. Naming a control and
       * highlighting a different one is the failure this whole tour is a correction for.
       *
       * Next, for exactly the reason the card above it is Next: the sheet does not require it.
       */
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'crew-signoff',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.crew-signoff',
      module: 'forms',
      permission: 'forms:submit',
      title: 'The roster does not fill itself in — read this one',
      body: 'Nothing fills this table in — not the crew you named in section 1, not your roster, nothing. Tap “+ Add row” once per person and pick each name by hand, or type it in the write-in column for a visitor or a sub. (If your company’s copy of this sheet still says the roster comes from the crew in section 1, that line is out of date: it never did.) Then tick each person off as you brief them — a tick, not a signature: a table in this app cannot hold one. And the box is a fixed height that does not scroll, so on a long crew the “+ Add row” button ends up below the bottom edge where you cannot reach it. Nobody warns you. If that happens, put the missing names in the scope-of-work box and tell the office.',
      // Same as `hazards`: the first draft wanted `rows >= 1`, and `answered` is that and stronger.
      // The first column of this table is the name picker, so this holds until one person has
      // actually been named — which is the thing the step is about, given that nothing populates it.
      advanceOn: ADVANCE_ON.DO,
      until: { answered: 'forms.fill.field.crew-signoff' },
      stuckHint: 'Add a row with “+ Add row”, then pick a name in the first column — or type one into the write-in column beside it.',
    },
    {
      id: 'one-signature',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.supervisor-signature',
      module: 'forms',
      permission: 'forms:submit',
      title: 'You sign last, and you sign alone',
      body: 'This is the only signature on the whole sheet, and it is required — yours, after you have briefed everyone. On a phone it is a small pad to sign with a finger; a tablet gives you room. Sign it, then press Save Signature under the pad: the sheet has nothing until you do, and the pad looks the same either way.',
      /**
       * NOW WAITS FOR AN ACTUAL SIGNATURE. This step was `advanceOn: CLICK` and its note said there
       * was nothing to observe — SignaturePad holds its ink in React state and swaps its <canvas> for
       * an <img>, neither of which carries an anchor, and `filled` reads the cell as permanently
       * empty because the field renders no form control at all.
       *
       * That was true of the DOM and false of the sheet. The pad's answer is a base64 PNG in
       * `payload_json`, and the payload is exactly what `answered` reads — so the one required box
       * that completes this document is held properly instead of being waved through by a tap on a
       * pad nobody drew on. It also holds against a pad that was drawn on and never SAVED, which is
       * the mistake the copy above now warns about, because until Save Signature is pressed the ink
       * exists only inside the widget.
       *
       * The old note argued the tap was safe because `send-it` would catch it. It would — as a 422
       * at the end of the walkthrough, which is the thing the owner reported.
       */
      advanceOn: ADVANCE_ON.DO,
      until: { answered: 'forms.fill.field.supervisor-signature' },
      stuckHint: 'Drawn on the pad and nothing happened? Press “Save Signature” underneath it — until you do, the sheet has no signature on it.',
    },
    {
      id: 'changed-conditions',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.field.conditions-changed',
      module: 'forms',
      permission: 'forms:submit',
      title: 'The last two questions, and they are the ones people scroll past',
      body: 'Has anything changed since you briefed the crew, and did anybody use stop-work authority today. Both are required, both are at the very bottom under the signature, and both default to nothing — so a sheet that looks finished is not. Answer yes to either and more boxes open underneath asking what changed, who was re-briefed, and what happened; those become required too.',
      /**
       * THE OTHER SECTION NO CARD EVER MENTIONED. Two required boxes, sitting below the signature
       * pad, on a sheet that is already too tall to scroll through on a tablet — the definition of
       * the box somebody misses. The old `one-signature` copy mentioned them in its last sentence and
       * gated on nothing, which is how a reader arrives at Submit with the sheet apparently complete.
       *
       * The three conditional follow-ups are in the set and are absent from the page unless the
       * reader opens them, so they hold nobody on the ordinary path and hold everybody on the path
       * where they are required.
       */
      advanceOn: ADVANCE_ON.DO,
      until: {
        answered: [
          'forms.fill.field.conditions-changed',
          'forms.fill.field.change-rebriefed',
          'forms.fill.field.conditions-changed-detail',
          'forms.fill.field.stop-work-used',
          'forms.fill.field.stop-work-detail',
        ],
      },
      stuckHint: 'These two are below the signature pad — scroll to the bottom of the sheet. Answering yes to either opens more boxes that also have to be filled in.',
    },
    {
      id: 'send-it',
      screen: '/forms/fill#jsa',
      anchor: 'forms.fill.submit',
      module: 'forms',
      permission: 'forms:submit',
      title: 'Send it',
      body: 'Seventeen boxes have to be answered before this will go, and the cards you have just been through are those seventeen — so this should go straight away. If it does not, the app checks them on your own screen first, highlights what is missing in red and scrolls you to the first one, so you are not waiting on signal to find out. When it goes through you get a number — #1, #2, counting up — and that number is your receipt: it is what somebody quotes months later when they ask about this sheet. It then sits marked Submitted. Sending it also pings the account administrators in the app — nobody else, and no email — so if a safety officer is meant to see these, somebody has to tell them.',
      // THE STEP THE WHOLE TOUR RESTS ON, so it waits for the request and not for the press. Pressing
      // Submit with a required box empty sends NOTHING: handleSubmit validates locally first and
      // returns without calling anything (FormFill.jsx:298-305). On a click gate that refusal still
      // walked the reader to the end and the launcher then reported the tour Finished to somebody
      // whose sheet had never left the tablet.
      //
      // IT IS NO LONGER THE ONLY THING HOLDING THE SHEET TOGETHER, and that is the change. It used
      // to be the backstop for four gates that could be satisfied without answering anything, so the
      // 422 it waited behind was where the reader found out — which is what the owner reported. Every
      // required box now has a card of its own ahead of this one, and the two remaining explain-only
      // cards above (`ppe-and-controls`, `site-controls`) point at the two fields the sheet does not
      // require. So this step should now satisfy on the first press, and the day it does not,
      // something above it has regressed.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /submit' },
      stuckHint: 'Red boxes at the top? Those are the required answers still missing — the sheet has not gone anywhere. Fill them and press Submit again.',
    },
    {
      id: 'find-it-again',
      // Screen '/forms' because the sub-nav is drawn above EVERY forms screen (FormsModule.jsx:76),
      // so this tab is on the sheet the user is still standing on. No `route` for the same reason:
      // sending them anywhere first would be a navigation they did not ask for, and the point of the
      // step is that they can get there from where they are.
      screen: '/forms',
      anchor: 'forms.nav.submissions',
      module: 'forms',
      title: 'Where it went',
      body: 'Every sheet anyone has filled in lives behind this tab. You always see your own; a foreman, a superintendent or the office sees everybody’s. This is where “show me the JSA for that job” gets answered, so it is worth opening once now rather than for the first time with somebody waiting on the phone.',
      // Waits for the LIST, because the three steps after it live on it.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'forms.submissions.filters' },
    },
    {
      id: 'what-you-can-filter-by',
      anchor: 'forms.submissions.filters',
      route: '/forms/submissions',
      module: 'forms',
      title: 'What you can search by — and what you cannot',
      body: 'Two filters: which state a sheet is in, and which form it is. That is the whole set. There is no date, no job, no crew and no search box, so “the JSA for Riverbend on the 9th” means setting the form filter and then paging down the list until you spot it. Know that before somebody asks you for one under pressure. Newest is at the top, and the date each sheet was sent is on its row.',
      // Explain-only: the step is about what these two dropdowns CANNOT do, and there is nothing to
      // perform. Setting a filter would teach nothing the sentence does not already say.
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'open-one',
      anchor: 'forms.submissions.row',
      route: '/forms/submissions',
      module: 'forms',
      title: 'Open one',
      body: 'Tap any row to read the whole sheet exactly as it was filled in — not as the form looks today. If somebody rewrites the JSA template next year, this one still reads the way the crew answered it, which is the only thing that makes it worth anything as a record.',
      // Waits for the SHEET to open, because the last step's button is on it.
      //
      // THE ONE STEP A PRACTICE RUN CAN LEGITIMATELY STRAND SOMEBODY ON, and it is named here rather
      // than discovered. The sheet the reader just "sent" was never created — that is the whole point
      // of a practice run — so this list holds whatever the tenant already had. On a tenant that has
      // never had a form filled in, there is no row to open and this step waits out its stuck timer.
      // Every earlier step is safe from it: the filter bar above renders whether or not the list has
      // anything in it. The hint says the true thing rather than the reassuring one.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'forms.submissions.pdf' },
      stuckHint: 'Nothing in the list to open? Then nobody at your company has sent one of these in yet — including you, because a walkthrough deliberately does not file a real sheet. Skip this step; it will be here the first time somebody really sends one.',
    },
    {
      id: 'the-pdf',
      // No `route`: this lives at /forms/submissions/<a real id>, reached by the tap above.
      screen: '/forms/submissions#detail',
      anchor: 'forms.submissions.pdf',
      module: 'forms',
      title: 'This is the copy you hand over',
      body: 'PDF downloads the sheet under your company’s name and logo — a file you can email to a client, an insurer or an inspector. That is the end of the trail. Nothing in the app ties this sheet to the job record, a time ticket or a service ticket, so the job name written on the sheet and the number on top of it are the whole of the link back. Which is worth knowing now rather than the day somebody asks for “the JSA for that job” and expects to find it on the job.',
      // The LAST card of the tour, so it waits for the download to have actually been asked for
      // rather than for the button to have been pressed. The PDF is generated server-side and the
      // button sits disabled while it is being built, so a press that produced no request is a press
      // that produced no file — and this being the final step, whatever it claims is what the reader
      // walks away believing. A read, so it goes out on a practice run like any other.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'GET /pdf' },
      stuckHint: 'Nothing downloaded? The sheet is being built server-side — give it a moment, and check your browser did not block the download.',
    },
  ],
})
